import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { partnerPb } from '../../../shared/api/partnerPocketbase';
import type { Partner } from '../../../entities/partner';

type PbRecord = Record<string, unknown>;

interface PartnerAuthContextValue {
  currentPartner: Partner | null;
  isAuthenticated: boolean;
  isReady: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const mapPartner = (record: PbRecord): Partner => ({
  id: record.id as string,
  email: record.email as string,
  name: (record.name as string) ?? '',
  isBlocked: Boolean(record.isBlocked),
  verified: Boolean(record.verified),
  created: (record.created as string) ?? '',
  updated: (record.updated as string) ?? '',
});

const getStoredPartner = (): Partner | null =>
  partnerPb.authStore.isValid && partnerPb.authStore.model
    ? mapPartner(partnerPb.authStore.model as unknown as PbRecord)
    : null;

const PartnerAuthContext = createContext<PartnerAuthContextValue | null>(null);

export const PartnerAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentPartner, setCurrentPartner] = useState<Partner | null>(getStoredPartner);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!partnerPb.authStore.isValid || !partnerPb.authStore.model) {
      setIsReady(true);
      return;
    }
    partnerPb.collection('partners')
      .authRefresh()
      .then(({ record }) => {
        const partner = mapPartner(record as unknown as PbRecord);
        if (partner.isBlocked) {
          partnerPb.authStore.clear();
          setCurrentPartner(null);
        } else {
          setCurrentPartner(partner);
        }
      })
      .catch(() => {
        partnerPb.authStore.clear();
        setCurrentPartner(null);
      })
      .finally(() => setIsReady(true));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { record } = await partnerPb.collection('partners').authWithPassword(email, password);
    const partner = mapPartner(record as unknown as PbRecord);
    if (partner.isBlocked) {
      partnerPb.authStore.clear();
      throw new Error('Ваш аккаунт заблокирован');
    }
    setCurrentPartner(partner);
  }, []);

  const logout = useCallback(() => {
    partnerPb.authStore.clear();
    setCurrentPartner(null);
  }, []);

  const value = useMemo<PartnerAuthContextValue>(() => ({
    currentPartner,
    isAuthenticated: currentPartner !== null,
    isReady,
    login,
    logout,
  }), [currentPartner, isReady, login, logout]);

  return <PartnerAuthContext.Provider value={value}>{children}</PartnerAuthContext.Provider>;
};

export const usePartnerAuth = (): PartnerAuthContextValue => {
  const ctx = useContext(PartnerAuthContext);
  if (!ctx) throw new Error('usePartnerAuth must be used inside PartnerAuthProvider');
  return ctx;
};
